import { Component, OnInit, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MusicService } from '../../services/music.service';
import { Track } from '../../models/track.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface VideoFile {
  name: string;
  url: string;
  duration: number;
  size: number;
}

@Component({
    selector: 'app-soundsvideo',
    imports: [CommonModule, FormsModule, TranslateModule, RouterLink],
    templateUrl: './soundsvideo.component.html',
    styleUrls: ['./soundsvideo.component.scss']
})
export class SoundsvideoComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoSection') videoSection!: ElementRef<HTMLElement>;

  // App state
  isFullscreen = false;
  showSidebar = true;
  activeTab: 'library' | 'upload' = 'library';

  // Video properties
  currentVideo: VideoFile | null = null;
  isVideoPlaying = false;
  videoDuration = 0;
  currentVideoTime = 0;
  videoProgress = 0;
  showControls = true;
  isDragOver = false;
  videoVolume = 0;
  isVideoLoop = true;  // Repeat activé par défaut
  showVideoControls = true;
  private hideControlsTimeout: any = null;

  // Music properties
  selectedTrack: Track | null = null;
  isMusicPlaying = false;
  isMusicLoop = true;  // Repeat musique activé par défaut
  musicVolume = 70;
  currentMusicTime = 0;
  musicProgress = 0;
  musicAudio: HTMLAudioElement | null = null;
  private isDraggingMusic = false;
  private musicBarRect: DOMRect | null = null;

  // Search & Filter
  searchQuery: string = '';
  selectedMood: string = 'all';
  moods: string[] = ['all', 'energetic', 'calm', 'happy', 'dramatic', 'epic', 'cinematic', 'ambient'];

  // Sync properties
  canPlayBoth = false;
  syncTolerance = 0.5;

  // Music library
  musicTracks: Track[] = [];

  // Modal states
  showTipsModal = false;
  showInfoModal = false;
  isClosing = false;

  // Mobile detection
  isMobile = false;

  constructor(
    private router: Router,
    private musicService: MusicService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    document.body.classList.add('soundsvideo-page');
    this.checkMobile();
    this.setupEventListeners();
    this.musicService.getTracks().subscribe(tracks => {
      this.musicTracks = tracks;
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('soundsvideo-page');
    this.cleanupAudio();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Keyboard shortcuts for desktop-like experience
    if (event.target instanceof HTMLInputElement) return;
    
    switch (event.code) {
      case 'Space':
        event.preventDefault();
        if (this.canPlayBoth) {
          this.togglePlayBoth();
        } else if (this.currentVideo) {
          this.toggleVideoPlay();
        }
        break;
      case 'KeyF':
        this.toggleFullscreen();
        break;
      case 'KeyS':
        this.toggleSidebar();
        break;
      case 'KeyR':
        this.resetBoth();
        break;
      case 'Escape':
        if (this.isFullscreen) this.toggleFullscreen();
        break;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    // Ne met à jour que isMobile, pas l'état du sidebar
    this.isMobile = window.innerWidth <= 768;
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
    // L'état du sidebar ne change qu'au premier chargement
    // Après, il reste tel quel même si on redimensionne la fenêtre
  }

  private setupEventListeners(): void {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, this.preventDefaults, false);
    });
  }

  private preventDefaults(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
  }

  private cleanupAudio(): void {
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.musicAudio = null;
    }
  }

  // UI Controls
  toggleSidebar(): void {
    this.showSidebar = !this.showSidebar;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      document.body.classList.add('fullscreen-mode');
    } else {
      document.body.classList.remove('fullscreen-mode');
    }
  }

  setActiveTab(tab: 'library' | 'upload'): void {
    this.activeTab = tab;
  }

  // File Upload Methods
  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleVideoFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('video/')) {
        this.handleVideoFile(file);
      } else {
        alert(this.translate.instant('SOUNDSVIDEO.ERRORS.INVALID_VIDEO'));
      }
    }
  }

  private handleVideoFile(file: File): void {
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(this.translate.instant('SOUNDSVIDEO.ERRORS.FILE_TOO_LARGE'));
      return;
    }

    const videoUrl = URL.createObjectURL(file);
    
    this.currentVideo = {
      name: file.name,
      url: videoUrl,
      duration: 0,
      size: file.size
    };

    this.isVideoPlaying = false;
    this.currentVideoTime = 0;
    this.videoProgress = 0;
    this.activeTab = 'library';
    this.updateCanPlayBoth();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Video Control Methods
  onVideoLoaded(): void {
    if (this.videoPlayer && this.currentVideo) {
      this.videoDuration = this.videoPlayer.nativeElement.duration;
      this.currentVideo.duration = this.videoDuration;
      this.videoPlayer.nativeElement.muted = true;
      this.videoVolume = 0;
      this.updateVideoVolume();
      
      const videoEl = this.videoPlayer.nativeElement;
      const w = videoEl.videoWidth || 16;
      const h = videoEl.videoHeight || 9;
      const ratio = `${w} / ${h}`;
      try {
        if (this.videoSection?.nativeElement) {
          this.videoSection.nativeElement.style.setProperty('--video-aspect-ratio', ratio);
        }
      } catch {}
      this.updateCanPlayBoth();
    }
  }

  onVideoTimeUpdate(): void {
    if (this.videoPlayer) {
      this.currentVideoTime = this.videoPlayer.nativeElement.currentTime;
      this.videoProgress = (this.currentVideoTime / this.videoDuration) * 100;
    }
  }

  onVideoEnded(): void {
    if (!this.isVideoLoop) {
      this.isVideoPlaying = false;
      this.pauseMusic();
    }
  }

  toggleVideoLoop(): void {
    this.isVideoLoop = !this.isVideoLoop;
  }

  enterVideoFullscreen(): void {
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if ((video as any).webkitRequestFullscreen) {
        (video as any).webkitRequestFullscreen();
      } else if ((video as any).msRequestFullscreen) {
        (video as any).msRequestFullscreen();
      }
    }
  }

  onVideoMouseEnter(): void {
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
    }
    this.showVideoControls = true;
  }

  onVideoMouseLeave(): void {
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
    }
    // Cacher immédiatement les contrôles au mouseleave
    this.showVideoControls = false;
  }

  onVideoMouseMove(): void {
    this.showVideoControls = true;
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
    }
    if (this.isVideoPlaying) {
      this.hideControlsTimeout = setTimeout(() => {
        this.showVideoControls = false;
      }, 2500);
    }
  }

  toggleVideoPlay(): void {
    if (!this.videoPlayer) return;

    if (this.isVideoPlaying) {
      this.videoPlayer.nativeElement.pause();
      this.isVideoPlaying = false;
    } else {
      this.videoPlayer.nativeElement.play();
      this.isVideoPlaying = true;
    }
  }

  seekVideo(event: MouseEvent): void {
    if (!this.videoPlayer) return;

    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const newTime = percent * this.videoDuration;
    
    this.videoPlayer.nativeElement.currentTime = newTime;
  }

  removeVideo(): void {
    if (this.currentVideo) {
      URL.revokeObjectURL(this.currentVideo.url);
      this.currentVideo = null;
      this.isVideoPlaying = false;
      this.pauseMusic();
      try {
        if (this.videoSection?.nativeElement) {
          this.videoSection.nativeElement.style.removeProperty('--video-aspect-ratio');
        }
      } catch {}
      this.updateCanPlayBoth();
    }
  }

  updateVideoVolume(): void {
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.volume = this.videoVolume / 100;
      this.videoPlayer.nativeElement.muted = this.videoVolume === 0;
    }
  }

  // Music Control Methods
  selectTrack(track: Track): void {
    if (this.selectedTrack?.id === track.id) return;

    this.pauseMusic();
    this.selectedTrack = track;
    this.setupMusicAudio();
    this.updateCanPlayBoth();

    // Fermer le volet musique sur mobile après sélection
    if (this.isMobile) {
      this.showSidebar = false;
    }
  }

  private setupMusicAudio(): void {
    if (!this.selectedTrack) return;

    this.cleanupAudio();
    this.musicAudio = new Audio(this.selectedTrack.url);
    this.musicAudio.volume = this.musicVolume / 100;
    this.musicAudio.loop = this.isMusicLoop;
    
    this.musicAudio.addEventListener('timeupdate', () => {
      if (this.musicAudio) {
        this.currentMusicTime = this.musicAudio.currentTime;
        this.musicProgress = (this.currentMusicTime / this.musicAudio.duration) * 100;
      }
    });

    this.musicAudio.addEventListener('ended', () => {
      if (!this.isMusicLoop) {
        this.isMusicPlaying = false;
        this.pauseVideo();
      }
    });
  }

  toggleMusicLoop(): void {
    this.isMusicLoop = !this.isMusicLoop;
    if (this.musicAudio) {
      this.musicAudio.loop = this.isMusicLoop;
    }
  }

  toggleMusicPlay(): void {
    if (!this.musicAudio) return;

    if (this.isMusicPlaying) {
      this.pauseMusic();
    } else {
      this.playMusic();
    }
  }

  private playMusic(): void {
    if (this.musicAudio) {
      this.musicAudio.play();
      this.isMusicPlaying = true;
    }
  }

  private pauseMusic(): void {
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.isMusicPlaying = false;
    }
  }

  private pauseVideo(): void {
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.pause();
      this.isVideoPlaying = false;
    }
  }

  updateMusicVolume(): void {
    if (this.musicAudio) {
      this.musicAudio.volume = this.musicVolume / 100;
    }
  }

  // Sync Control Methods
  togglePlayBoth(): void {
    if (this.isVideoPlaying && this.isMusicPlaying) {
      this.pauseBoth();
    } else {
      this.playBoth();
    }
  }

  playBoth(): void {
    if (!this.canPlayBoth) return;

    if (this.videoPlayer && this.musicAudio) {
      this.videoPlayer.nativeElement.play();
      this.musicAudio.play();
      this.isVideoPlaying = true;
      this.isMusicPlaying = true;
    }
  }

  pauseBoth(): void {
    this.pauseVideo();
    this.pauseMusic();
  }

  resetBoth(): void {
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.currentTime = 0;
    }
    if (this.musicAudio) {
      this.musicAudio.currentTime = 0;
    }
    this.pauseBoth();
  }

  private updateCanPlayBoth(): void {
    this.canPlayBoth = !!(this.currentVideo && this.selectedTrack && this.videoDuration > 0);
  }

  // Music timeline seeking/dragging
  seekMusic(event: MouseEvent | TouchEvent): void {
    if (!this.musicAudio) return;
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const clientX = (event as TouchEvent).touches?.[0]?.clientX ?? (event as MouseEvent).clientX;
    const percent = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const newTime = percent * (this.musicAudio.duration || 0);
    this.musicAudio.currentTime = newTime;
    this.currentMusicTime = newTime;
    this.musicProgress = percent * 100;
  }

  startMusicDrag(event: MouseEvent | TouchEvent): void {
    if (!this.musicAudio) return;
    event.preventDefault();
    const target = (event.currentTarget as HTMLElement).closest('.timeline-bar') as HTMLElement | null;
    if (!target) return;
    this.isDraggingMusic = true;
    this.musicBarRect = target.getBoundingClientRect();
    this.onMusicDrag(event);
    window.addEventListener('mousemove', this.onMusicDragBound);
    window.addEventListener('mouseup', this.endMusicDragBound);
    window.addEventListener('touchmove', this.onMusicDragBound, { passive: false });
    window.addEventListener('touchend', this.endMusicDragBound, { passive: false });
  }

  private onMusicDragBound = (e: MouseEvent | TouchEvent) => this.onMusicDrag(e);
  private endMusicDragBound = () => this.endMusicDrag();

  onMusicDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDraggingMusic || !this.musicAudio || !this.musicBarRect) return;
    event.preventDefault();
    const clientX = (event as TouchEvent).touches?.[0]?.clientX ?? (event as MouseEvent).clientX;
    const percent = Math.min(1, Math.max(0, (clientX - this.musicBarRect.left) / this.musicBarRect.width));
    const newTime = percent * (this.musicAudio.duration || 0);
    this.musicAudio.currentTime = newTime;
    this.currentMusicTime = newTime;
    this.musicProgress = percent * 100;
  }

  endMusicDrag(): void {
    if (!this.isDraggingMusic) return;
    this.isDraggingMusic = false;
    this.musicBarRect = null;
    window.removeEventListener('mousemove', this.onMusicDragBound);
    window.removeEventListener('mouseup', this.endMusicDragBound);
    window.removeEventListener('touchmove', this.onMusicDragBound as any);
    window.removeEventListener('touchend', this.endMusicDragBound as any);
  }

  // Video timeline dragging
  private isDraggingVideo = false;
  private videoBarRect: DOMRect | null = null;

  startVideoDrag(event: MouseEvent | TouchEvent): void {
    if (!this.videoPlayer) return;
    event.preventDefault();
    const target = (event.currentTarget as HTMLElement).closest('.timeline-bar') as HTMLElement | null;
    if (!target) return;
    this.isDraggingVideo = true;
    this.videoBarRect = target.getBoundingClientRect();
    this.onVideoDrag(event);
    window.addEventListener('mousemove', this.onVideoDragBound);
    window.addEventListener('mouseup', this.endVideoDragBound);
    window.addEventListener('touchmove', this.onVideoDragBound, { passive: false });
    window.addEventListener('touchend', this.endVideoDragBound, { passive: false });
  }

  private onVideoDragBound = (e: MouseEvent | TouchEvent) => this.onVideoDrag(e);
  private endVideoDragBound = () => this.endVideoDrag();

  onVideoDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDraggingVideo || !this.videoPlayer || !this.videoBarRect) return;
    event.preventDefault();
    const clientX = (event as TouchEvent).touches?.[0]?.clientX ?? (event as MouseEvent).clientX;
    const percent = Math.min(1, Math.max(0, (clientX - this.videoBarRect.left) / this.videoBarRect.width));
    const newTime = percent * (this.videoDuration || 0);
    this.videoPlayer.nativeElement.currentTime = newTime;
    this.currentVideoTime = newTime;
    this.videoProgress = percent * 100;
  }

  endVideoDrag(): void {
    if (!this.isDraggingVideo) return;
    this.isDraggingVideo = false;
    this.videoBarRect = null;
    window.removeEventListener('mousemove', this.onVideoDragBound);
    window.removeEventListener('mouseup', this.endVideoDragBound);
    window.removeEventListener('touchmove', this.onVideoDragBound as any);
    window.removeEventListener('touchend', this.endVideoDragBound as any);
  }

  // Utility Methods
  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // Filtered and sorted tracks
  get filteredTracks(): Track[] {
    let tracks = this.musicTracks;
    
    // Filter by mood
    if (this.selectedMood !== 'all') {
      tracks = tracks.filter(t => 
        Array.isArray(t.mood) && t.mood.includes(this.selectedMood)
      );
    }
    
    // Filter by search query
    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      tracks = tracks.filter(t =>
        t.title.toLowerCase().includes(q) || t.artist.toLowerCase().includes(q)
      );
    }
    
    return tracks;
  }

  getTrackGradient(track: Track): string {
    const byMood = (m: string) => {
      const gradients: { [key: string]: string } = {
        energetic: 'linear-gradient(45deg, #ff6b6b, #ff8e8e)',
        calm: 'linear-gradient(45deg, #4ecdc4, #44a08d)',
        happy: 'linear-gradient(45deg, #feca57, #ff9ff3)',
        dramatic: 'linear-gradient(45deg, #5f27cd, #341f97)',
        epic: 'linear-gradient(45deg, #00d2d3, #54a0ff)',
        cinematic: 'linear-gradient(45deg, #2c3e50, #4ca1af)',
        ambient: 'linear-gradient(45deg, #667eea, #764ba2)'
      };
      return gradients[m];
    };
    const mood = Array.isArray(track.mood) ? track.mood.find(m => byMood(m)) : null;
    if (mood) return byMood(mood)!;
    return 'linear-gradient(45deg, #667eea, #764ba2)';
  }

  getSyncStatusClass(): string {
    if (!this.canPlayBoth) return 'ready';
    const timeDiff = Math.abs(this.currentVideoTime - this.currentMusicTime);
    return timeDiff <= this.syncTolerance ? 'synced' : 'out-of-sync';
  }

  getSyncStatusText(): string {
    if (!this.canPlayBoth) return this.translate.instant('SOUNDSVIDEO.SYNC.STATUS.READY');
    const timeDiff = Math.abs(this.currentVideoTime - this.currentMusicTime);
    return timeDiff <= this.syncTolerance
      ? this.translate.instant('SOUNDSVIDEO.SYNC.STATUS.SYNCED')
      : this.translate.instant('SOUNDSVIDEO.SYNC.STATUS.OUT_OF_SYNC');
  }

  // Modals
  openTipsModal(): void {
    this.isClosing = false;
    this.showTipsModal = true;
  }

  closeTipsModal(): void {
    this.isClosing = true;
    setTimeout(() => {
      this.showTipsModal = false;
      this.isClosing = false;
    }, 250);
  }

  openInfoModal(): void {
    this.isClosing = false;
    this.showInfoModal = true;
  }

  closeInfoModal(): void {
    this.isClosing = true;
    setTimeout(() => {
      this.showInfoModal = false;
      this.isClosing = false;
    }, 250);
  }

  // Actions
  orderCustomMusic(): void {
    const projectData = {
      videoName: this.currentVideo?.name,
      selectedMusic: this.selectedTrack?.title,
      videoDuration: this.formatTime(this.videoDuration)
    };
    
    sessionStorage.setItem('soundsvideoProject', JSON.stringify(projectData));
    this.router.navigate(['/']);
    
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openOrderForm', { 
        detail: { serviceType: 'soundtrack' } 
      }));
    }, 1000);
  }
}
